// Coach-Governed Continuity Loop — carry-forward candidate card.
// Hard boundaries: no scoring, no evaluation, no persistence, no DB writes.
// Actions are local UI state only. Do NOT label actions as "Saved" or "Remembered".
// Coach-first rule: Never surface these outputs to leaders.
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import type { CarryForwardCandidate, CarryForwardAction } from "@/types/carryForward";

interface CarryForwardCandidateCardProps {
  candidate: CarryForwardCandidate;
  actionTaken?: CarryForwardAction;
  onAction: (candidateId: string, action: CarryForwardAction, reframedTitle?: string) => void;
  mode?: "leader" | "coach_practice";
}

const DIM_ACTIONS: CarryForwardAction[] = ["retire", "ignore_for_now"];

const ACTION_LABEL: Record<CarryForwardAction, string> = {
  carry_forward: "carry forward",
  reframe: "reframe",
  retire: "retire",
  ignore_for_now: "ignore for now",
};

export function CarryForwardCandidateCard({
  candidate,
  actionTaken,
  onAction,
  mode = "leader",
}: CarryForwardCandidateCardProps) {
  const [showReframeInput, setShowReframeInput] = useState(false);
  const [reframeText, setReframeText] = useState("");

  const isDimmed = actionTaken !== undefined && DIM_ACTIONS.includes(actionTaken);

  return (
    <div
      className={cn(
        "rounded-lg border border-border bg-card p-3 space-y-2 transition-opacity",
        isDimmed && "opacity-60",
      )}
    >
      {/* Title */}
      <p className="text-sm font-semibold text-foreground">{candidate.title}</p>

      {/* Observed moment */}
      <div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Observed moment</p>
        <p className="mt-0.5 text-sm text-muted-foreground">{candidate.observedMoment}</p>
      </div>

      {/* Why surfaced */}
      <div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">
          {mode === "coach_practice" ? "Why this surfaced" : "Why Veera surfaced this"}
        </p>
        <p className="mt-0.5 text-sm">{candidate.whySurfaced}</p>
      </div>

      {/* Evidence */}
      {candidate.evidenceFragments.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Evidence</p>
          <ul className="mt-0.5 space-y-0.5">
            {candidate.evidenceFragments.map((fragment, i) => {
              const label =
                fragment.title ?? fragment.displayLabel ?? fragment.sourceId;
              return (
                <li key={i} className="flex gap-2 text-sm">
                  <span
                    className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/50"
                    aria-hidden="true"
                  />
                  <span>{label}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* What not to assume */}
      {candidate.whatNotToAssume.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">What not to assume</p>
          <ul className="mt-0.5 space-y-0.5">
            {candidate.whatNotToAssume.map((item, i) => (
              <li key={i} className="text-sm text-muted-foreground">
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Suggested question */}
      {candidate.suggestedQuestion && (
        <div className="rounded-lg border border-violet-500/20 bg-violet-500/5 p-2">
          <p className="text-xs uppercase tracking-wide text-violet-400">
            {mode === "coach_practice" ? "Practice question" : "Coach question"}
          </p>
          <p className="mt-0.5 text-sm italic text-foreground/90">
            &ldquo;{candidate.suggestedQuestion}&rdquo;
          </p>
        </div>
      )}

      {/* Missing data */}
      {candidate.missingData.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Missing data</p>
          <ul className="mt-0.5 space-y-0.5">
            {candidate.missingData.map((item, i) => (
              <li key={i} className="flex gap-2 text-sm text-muted-foreground">
                <span
                  className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/50"
                  aria-hidden="true"
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Action row */}
      {actionTaken !== undefined ? (
        <p className="text-xs text-muted-foreground">
          Marked: {ACTION_LABEL[actionTaken]}
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5 pt-1">
            <Button
              type="button"
              size="sm"
              className="h-7 rounded-full px-3 text-xs"
              onClick={() => onAction(candidate.id, "carry_forward")}
              aria-label={`Carry forward: ${candidate.title}`}
            >
              Carry forward
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 rounded-full px-3 text-xs"
              onClick={() => {
                setReframeText(candidate.title);
                setShowReframeInput(true);
              }}
              aria-label={`Reframe: ${candidate.title}`}
            >
              Reframe
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 rounded-full px-3 text-xs"
              onClick={() => onAction(candidate.id, "retire")}
              aria-label={`Retire: ${candidate.title}`}
            >
              Retire
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 rounded-full px-3 text-xs"
              onClick={() => onAction(candidate.id, "ignore_for_now")}
              aria-label={`Ignore for now: ${candidate.title}`}
            >
              Ignore for now
            </Button>
          </div>
          {showReframeInput && (
            <div className="flex flex-col gap-1.5 pt-1">
              <input
                type="text"
                className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                value={reframeText}
                onChange={(e) => setReframeText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    onAction(candidate.id, "reframe", reframeText);
                    setShowReframeInput(false);
                  } else if (e.key === "Escape") {
                    setShowReframeInput(false);
                  }
                }}
                autoFocus
                aria-label="Reframed title"
              />
              <div className="flex gap-1.5">
                <Button
                  type="button"
                  size="sm"
                  className="h-7 rounded-full px-3 text-xs"
                  onClick={() => {
                    onAction(candidate.id, "reframe", reframeText);
                    setShowReframeInput(false);
                  }}
                >
                  Confirm
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 rounded-full px-3 text-xs"
                  onClick={() => setShowReframeInput(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
